import { gql } from '@apollo/client';

export const ADMIN_LOGIN_MUTATION = gql`
  mutation AdminLogin($data: AdminLoginInput!) {
    adminLogin(data: $data) {
      access_token
      employee {
        id
        firstName
        lastName
        email
        role
        roleId
        roleName
        roleNames
        roleIds
        permissions
      }
    }
  }
`;

export const ADMIN_LOGOUT_MUTATION = gql`
  mutation AdminLogout {
    adminLogout
  }
`;

export const GET_EMPLOYEE_BASIC_INFO = gql`
  query GetEmployeeBasicInfo($id: String!) {
    employee(id: $id) {
      id
      firstName
      lastName
      email
      department
      roleName
      position {
        id
        name
      }
    }
  }
`;

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      firstName
      lastName
      email
      pin
      role
      roleId
      roleName
      roleNames
      roleIds
      permissions
      isActive
      department
      position {
        id
        name
        department
      }
      hireDate
      createdAt
    }
  }
`;

export const GET_EMPLOYEES_WITH_PERMISSION = gql`
  query GetEmployeesWithPermission($permission: String!) {
    employeesWithPermission(permission: $permission) {
      id
      firstName
      lastName
      email
      isActive
    }
  }
`;

export const GET_ALL_EMPLOYEES = gql`
  query GetAllEmployees {
    employees {
      id
      firstName
      lastName
      email
      login
      pin
      employeeCode
      role
      roleId
      roleName
      roleNames
      roleIds
      isActive
      department
      departmentId
      position {
        id
        name
        department
      }
      shift {
        id
        name
        isDefault
      }
      contractType {
        id
        displayName
        hasOvertimePay
      }
      disabilityDegreeId
      disabilityDegree {
        id
        name
        maxWorkHoursPerDay
        breakDurationMinutes
        additionalBreakMinutes
      }
      vacationDays
      hireDate
      terminationDate
      createdAt
      autoCloseEnabled
      autoCloseTime
      autoCloseType
      autoCloseAfterHours
    }
  }
`;

export const GET_PROJECT_ASSIGNABLE_EMPLOYEES = gql`
  query GetProjectAssignableEmployees($projectId: String!) {
    projectAssignableEmployees(projectId: $projectId) {
      id
      firstName
      lastName
      isActive
    }
  }
`;

export const GET_ASSIGNABLE_PROJECT_LEADERS = gql`
  query GetAssignableProjectLeaders {
    assignableProjectLeaders {
      id
      firstName
      lastName
      isActive
    }
  }
`;

export const GET_EMPLOYEES_PAGINATED = gql`
  query GetEmployeesPaginated(
    $page: Int
    $pageSize: Int
    $sortBy: String
    $sortOrder: String
    $searchTerm: String
    $isActive: Boolean
  ) {
    employeesPaginated(
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
      searchTerm: $searchTerm
      isActive: $isActive
    ) {
      employees {
        id
        firstName
        lastName
        email
        login
        pin
        employeeCode
        role
        roleId
        roleName
        roleNames
        isActive
        department
        departmentId
        position {
          id
          name
          department
        }
        shift {
          id
          name
          isDefault
        }
        contractType {
          id
          displayName
          hasOvertimePay
        }
        disabilityDegreeId
        disabilityDegree {
          id
          name
          maxWorkHoursPerDay
          breakDurationMinutes
          additionalBreakMinutes
        }
        hireDate
        terminationDate
        autoCloseEnabled
        autoCloseTime
        autoCloseType
        autoCloseAfterHours
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export const GET_ALL_EVENTS = gql`
  query GetAllEvents {
    events {
      id
      timestamp
      note
      employee {
        id
        firstName
        lastName
        department
      }
      eventType {
        id
        name
        code
        color
      }
    }
  }
`;

export const GET_EVENTS_PAGINATED = gql`
  query GetEventsPaginated(
    $page: Int
    $pageSize: Int
    $sortBy: String
    $sortOrder: String
    $searchTerm: String
    $dateFilter: String
    $employeeId: String
    $startDate: DateTime
    $endDate: DateTime
    $eventTypeIds: [String!]
  ) {
    eventsPaginated(
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
      searchTerm: $searchTerm
      dateFilter: $dateFilter
      employeeId: $employeeId
      startDate: $startDate
      endDate: $endDate
      eventTypeIds: $eventTypeIds
    ) {
      events {
        id
        timestamp
        note
        duration
        employee {
          id
          firstName
          lastName
          department
        }
        shiftId
        snapshotShiftName
        expectedShiftStart
        expectedShiftEnd
        eventType {
          id
          name
          code
          color
        }
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export const GET_RECENT_EVENTS = gql`
  query GetRecentEvents($limit: Float) {
    recentEvents(limit: $limit) {
      id
      timestamp
      note
      duration
      employee {
        id
        firstName
        lastName
        department
      }
      eventType {
        id
        name
        code
        color
      }
    }
  }
`;

export const GET_EMPLOYEE_REPORT = gql`
  query GetEmployeeReport($employeeId: String!, $startDate: DateTime!, $endDate: DateTime!) {
    employeeReport(employeeId: $employeeId, startDate: $startDate, endDate: $endDate) {
      employeeId
      startDate
      endDate
      totalEvents
      events {
        id
        timestamp
        eventType {
          name
          code
          color
        }
      }
    }
  }
`;

export const GET_DAILY_REPORT = gql`
  query GetDailyReport($date: DateTime!) {
    dailyReport(date: $date) {
      date
      totalEvents
      events {
        id
        timestamp
        employee {
          firstName
          lastName
          department
        }
        eventType {
          name
          code
          color
        }
      }
    }
  }
`;

export const GET_EVENT_TYPES = gql`
  query GetEventTypes {
    eventTypes {
      id
      name
      code
      description
      color
      icon
      order
      isActive
    }
  }
`;

export const UPDATE_EVENT_MUTATION = gql`
  mutation UpdateEvent(
    $id: String!
    $timestamp: DateTime
    $note: String
    $eventTypeId: String
    $recalculateSnapshot: Boolean
    $shiftId: String
    $snapshotShiftName: String
    $expectedShiftStart: String
    $expectedShiftEnd: String
  ) {
    updateEvent(
      id: $id
      timestamp: $timestamp
      note: $note
      eventTypeId: $eventTypeId
      recalculateSnapshot: $recalculateSnapshot
      shiftId: $shiftId
      snapshotShiftName: $snapshotShiftName
      expectedShiftStart: $expectedShiftStart
      expectedShiftEnd: $expectedShiftEnd
    ) {
      id
      timestamp
      note
      shiftId
      snapshotShiftName
      expectedShiftStart
      expectedShiftEnd
      employee {
        id
        firstName
        lastName
        department
      }
      eventType {
        id
        name
        code
        color
      }
    }
  }
`;

export const DELETE_EVENT_MUTATION = gql`
  mutation RemoveEvent($id: String!) {
    removeEvent(id: $id)
  }
`;

export const CREATE_EMPLOYEE_MUTATION = gql`
  mutation CreateEmployee($data: CreateEmployeeInput!) {
    createEmployee(data: $data) {
      id
      firstName
      lastName
      email
      login
      pin
      employeeCode
      role
      roleId
      roleName
      roleNames
      roleIds
      department
      departmentId
      position {
        id
        name
        department
      }
      shift {
        id
        name
        isDefault
      }
      contractType {
        id
        displayName
        hasOvertimePay
      }
      disabilityDegreeId
      disabilityDegree {
        id
        name
      }
      hireDate
      terminationDate
      autoCloseEnabled
      autoCloseTime
      autoCloseType
      autoCloseAfterHours
    }
  }
`;

export const UPDATE_EMPLOYEE_MUTATION = gql`
  mutation UpdateEmployee($id: String!, $data: UpdateEmployeeInput!) {
    updateEmployee(id: $id, data: $data) {
      id
      firstName
      lastName
      email
      login
      pin
      employeeCode
      role
      roleId
      roleName
      roleNames
      roleIds
      department
      departmentId
      position {
        id
        name
        department
      }
      shift {
        id
        name
        isDefault
      }
      contractType {
        id
        displayName
        hasOvertimePay
      }
      disabilityDegreeId
      disabilityDegree {
        id
        name
      }
      isActive
      vacationDays
      hireDate
      terminationDate
      autoCloseEnabled
      autoCloseTime
      autoCloseType
      autoCloseAfterHours
    }
  }
`;

export const GET_TIME_REPORT = gql`
  query GetTimeReport($startDate: DateTime!, $endDate: DateTime!, $employeeId: String, $applyTolerances: Boolean, $employeeIds: [String!]) {
    timeReport(startDate: $startDate, endDate: $endDate, employeeId: $employeeId, applyTolerances: $applyTolerances, employeeIds: $employeeIds) {
      employee {
        id
        firstName
        lastName
        department
        position
      }
      workHours
      breakHours
      smokeHours
      overtime50
      overtime100
      totalEvents
      leaveDays
      leaveDetails {
        leaveType
        displayName
        color
        days
      }
      startDate
      endDate
    }
  }
`;

export const GET_LEAVES_BY_DATE_RANGE = gql`
  query GetLeavesByDateRange($startDate: DateTime!, $endDate: DateTime!) {
    leavesByDateRange(startDate: $startDate, endDate: $endDate) {
      id
      employeeId
      leaveType
      startDate
      endDate
      status
      reason
      approvedBy
      approvedAt
      createdAt
      employee {
        id
        firstName
        lastName
        department
        position
      }
    }
  }
`;

export const GET_PROJECTS_BY_DATE_RANGE = gql`
  query GetProjectsByDateRange($startDate: String!, $endDate: String!) {
    projectsByDateRange(startDate: $startDate, endDate: $endDate) {
      id
      name
      status
      startDate
      plannedEndDate
      leaderId
      leader {
        id
        firstName
        lastName
      }
    }
  }
`;

export const GET_TASKS_BY_DATE_RANGE = gql`
  query GetTasksByDateRange($startDate: String!, $endDate: String!) {
    tasksByDateRange(startDate: $startDate, endDate: $endDate) {
      id
      projectId
      name
      priority
      startDate
      plannedEndDate
      project {
        id
        name
        status
      }
    }
  }
`;

export const GET_EMPLOYEE_LEAVES = gql`
  query GetEmployeeLeaves($employeeId: String!) {
    employeeLeaves(employeeId: $employeeId) {
      id
      employeeId
      leaveType
      startDate
      endDate
      status
      reason
      approvedBy
      approvedAt
      createdAt
      employee {
        id
        firstName
        lastName
        department
        position
      }
    }
  }
`;

export const GET_VACATION_STATS = gql`
  query GetVacationStats($employeeId: String!, $year: Int!) {
    vacationStats(employeeId: $employeeId, year: $year) {
      totalDays
      usedDays
      pendingDays
      availableDays
    }
  }
`;

export const GET_ALL_LEAVES = gql`
  query GetAllLeaves {
    leaves {
      id
      employeeId
      leaveType
      startDate
      endDate
      status
      reason
      approvedBy
      approvedAt
      createdAt
      employee {
        id
        firstName
        lastName
        department
        position
      }
    }
  }
`;

// Device Management GraphQL Queries
export const GET_ALL_DEVICES = gql`
  query GetAllDevices {
    devices {
      id
      deviceName
      location
      token
      fingerprint
      status
      userAgent
      screenResolution
      timezone
      ipAddress
      lastSeen
      macAddress
      serialNumber
      notes
      allowedIpRanges
      workingHours
      allowedDays
      approvedBy
      approvedAt
      registeredAt
      createdAt
      updatedAt
      approver {
        id
        firstName
        lastName
      }
    }
  }
`;

export const CREATE_DEVICE_MUTATION = gql`
  mutation CreateDeviceNew(
    $deviceName: String!
    $location: String!
    $notes: String
  ) {
    createDeviceNew(
      deviceName: $deviceName
      location: $location
      notes: $notes
    ) {
      id
      deviceName
      location
      token
      status
      allowedIpRanges
      workingHours
      allowedDays
      notes
      createdAt
    }
  }
`;

export const UPDATE_DEVICE_MUTATION = gql`
  mutation UpdateDevice(
    $id: String!
    $deviceName: String
    $location: String
    $notes: String
    $workingHours: String
    $allowedDays: [String!]
    $allowedIpRanges: [String!]
  ) {
    updateDevice(
      id: $id
      deviceName: $deviceName
      location: $location
      notes: $notes
      workingHours: $workingHours
      allowedDays: $allowedDays
      allowedIpRanges: $allowedIpRanges
    ) {
      id
      deviceName
      location
      notes
      allowedIpRanges
      workingHours
      allowedDays
      updatedAt
    }
  }
`;

export const APPROVE_DEVICE_MUTATION = gql`
  mutation ApproveDevice($deviceId: String!) {
    approveDevice(deviceId: $deviceId) {
      id
      status
      approvedBy
      approvedAt
    }
  }
`;

export const REJECT_DEVICE_MUTATION = gql`
  mutation RejectDevice($deviceId: String!, $reason: String) {
    rejectDevice(deviceId: $deviceId, reason: $reason) {
      id
      status
      notes
    }
  }
`;

export const BLOCK_DEVICE_MUTATION = gql`
  mutation BlockDevice($deviceId: String!, $reason: String) {
    blockDevice(deviceId: $deviceId, reason: $reason) {
      id
      status
      notes
    }
  }
`;

export const DELETE_DEVICE_MUTATION = gql`
  mutation DeleteDevice($id: String!) {
    deleteDevice(id: $id) {
      id
    }
  }
`;

export const GET_DEVICE_LOGS = gql`
  query GetDeviceLogs($deviceId: String!, $limit: Int, $offset: Int, $eventFilter: [DeviceLogEvent!]) {
    deviceLogs(deviceId: $deviceId, limit: $limit, offset: $offset, eventFilter: $eventFilter) {
      logs {
        id
        deviceName
        event
        success
        reason
        ipAddress
        userAgent
        performedBy
        metadata
        createdAt
      }
      total
    }
  }
`;

export const CREATE_LEAVE_MUTATION = gql`
  mutation CreateLeave($employeeId: String!, $leaveType: String!, $startDate: DateTime!, $endDate: DateTime!, $reason: String) {
    createLeave(employeeId: $employeeId, leaveType: $leaveType, startDate: $startDate, endDate: $endDate, reason: $reason) {
      id
      employeeId
      leaveType
      startDate
      endDate
      status
      reason
      createdAt
      employee {
        id
        firstName
        lastName
        department
        position
      }
    }
  }
`;

export const UPDATE_LEAVE_MUTATION = gql`
  mutation UpdateLeave($id: String!, $leaveType: String, $startDate: DateTime, $endDate: DateTime, $status: LeaveStatus, $reason: String, $approvedBy: String) {
    updateLeave(id: $id, leaveType: $leaveType, startDate: $startDate, endDate: $endDate, status: $status, reason: $reason, approvedBy: $approvedBy) {
      id
      employeeId
      leaveType
      startDate
      endDate
      status
      reason
      approvedBy
      approvedAt
      updatedAt
      employee {
        id
        firstName
        lastName
        department
        position
      }
    }
  }
`;

export const DELETE_LEAVE_MUTATION = gql`
  mutation DeleteLeave($id: String!) {
    deleteLeave(id: $id) {
      id
    }
  }
`;

export const GET_LEAVE_TYPES = gql`
  query GetLeaveTypes {
    leaveTypes {
      id
      code
      shortCode
      value
      label
      displayName
      color
      description
      requiresApproval
      countsAgainstLimit
      isActive
      sortOrder
    }
  }
`;

export const GET_LEAVE_TYPE_STATISTICS = gql`
  query GetLeaveTypeStatistics {
    leaveTypeStatistics {
      id
      code
      shortCode
      value
      label
      displayName
      color
      description
      requiresApproval
      countsAgainstLimit
      isActive
      sortOrder
      usageCount
    }
  }
`;

export const CREATE_LEAVE_TYPE_MUTATION = gql`
  mutation CreateLeaveType(
    $shortCode: String!
    $displayName: String!
    $color: String
    $description: String
    $requiresApproval: Boolean
    $countsAgainstLimit: Boolean
    $isActive: Boolean
    $sortOrder: Int
  ) {
    createLeaveType(
      shortCode: $shortCode
      displayName: $displayName
      color: $color
      description: $description
      requiresApproval: $requiresApproval
      countsAgainstLimit: $countsAgainstLimit
      isActive: $isActive
      sortOrder: $sortOrder
    ) {
      id
      code
      shortCode
      displayName
      color
      description
      requiresApproval
      countsAgainstLimit
      isActive
      sortOrder
    }
  }
`;

export const UPDATE_LEAVE_TYPE_MUTATION = gql`
  mutation UpdateLeaveType(
    $id: String!
    $shortCode: String
    $displayName: String
    $color: String
    $description: String
    $requiresApproval: Boolean
    $countsAgainstLimit: Boolean
    $isActive: Boolean
    $sortOrder: Int
  ) {
    updateLeaveType(
      id: $id
      shortCode: $shortCode
      displayName: $displayName
      color: $color
      description: $description
      requiresApproval: $requiresApproval
      countsAgainstLimit: $countsAgainstLimit
      isActive: $isActive
      sortOrder: $sortOrder
    ) {
      id
      code
      shortCode
      displayName
      color
      description
      requiresApproval
      countsAgainstLimit
      isActive
      sortOrder
    }
  }
`;

export const DELETE_LEAVE_TYPE_MUTATION = gql`
  mutation DeleteLeaveType($id: String!) {
    deleteLeaveType(id: $id) {
      id
    }
  }
`;

// Contract Type queries and mutations
export const GET_CONTRACT_TYPES = gql`
  query GetContractTypes {
    contractTypes {
      id
      code
      displayName
      description
      hasOvertimePay
      isActive
      sortOrder
    }
  }
`;

export const GET_CONTRACT_TYPE_STATISTICS = gql`
  query GetContractTypeStatistics {
    contractTypeStatistics {
      id
      code
      displayName
      description
      hasOvertimePay
      isActive
      sortOrder
      usageCount
    }
  }
`;

export const CREATE_CONTRACT_TYPE_MUTATION = gql`
  mutation CreateContractType(
    $code: String!
    $displayName: String!
    $description: String
    $hasOvertimePay: Boolean
    $isActive: Boolean
    $sortOrder: Int
  ) {
    createContractType(
      code: $code
      displayName: $displayName
      description: $description
      hasOvertimePay: $hasOvertimePay
      isActive: $isActive
      sortOrder: $sortOrder
    ) {
      id
      code
      displayName
      description
      hasOvertimePay
      isActive
      sortOrder
    }
  }
`;

export const UPDATE_CONTRACT_TYPE_MUTATION = gql`
  mutation UpdateContractType(
    $id: String!
    $code: String
    $displayName: String
    $description: String
    $hasOvertimePay: Boolean
    $isActive: Boolean
    $sortOrder: Int
  ) {
    updateContractType(
      id: $id
      code: $code
      displayName: $displayName
      description: $description
      hasOvertimePay: $hasOvertimePay
      isActive: $isActive
      sortOrder: $sortOrder
    ) {
      id
      code
      displayName
      description
      hasOvertimePay
      isActive
      sortOrder
    }
  }
`;

export const DELETE_CONTRACT_TYPE_MUTATION = gql`
  mutation DeleteContractType($id: String!) {
    deleteContractType(id: $id) {
      id
    }
  }
`;

export const GET_POSITIONS = gql`
  query GetPositions {
    positions {
      id
      name
      description
      department
      departmentId
      isActive
      employeeCount
      createdAt
      updatedAt
    }
  }
`;

export const GET_POSITIONS_PAGINATED = gql`
  query GetPositionsPaginated(
    $page: Int
    $pageSize: Int
    $sortBy: String
    $sortOrder: String
    $searchTerm: String
  ) {
    positionsPaginated(
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
      searchTerm: $searchTerm
    ) {
      positions {
        id
        name
        description
        department
        departmentId
        defaultShiftId
        defaultShiftName
        isActive
        employeeCount
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export const GET_POSITION_STATISTICS = gql`
  query GetPositionStatistics {
    positionStatistics {
      totalPositions
      activePositions
      positionsWithEmployees
      emptyPositions
    }
  }
`;

export const CREATE_POSITION_MUTATION = gql`
  mutation CreatePosition($data: CreatePositionInput!) {
    createPosition(data: $data) {
      id
      name
      description
      department
      departmentId
      defaultShiftId
      defaultShiftName
      isActive
      employeeCount
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_POSITION_MUTATION = gql`
  mutation UpdatePosition($id: String!, $data: UpdatePositionInput!) {
    updatePosition(id: $id, data: $data) {
      id
      name
      description
      department
      departmentId
      defaultShiftId
      defaultShiftName
      isActive
      employeeCount
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_POSITION_MUTATION = gql`
  mutation DeletePosition($id: String!) {
    deletePosition(id: $id) {
      id
      name
    }
  }
`;

export const CREATE_EVENT_MUTATION = gql`
  mutation CreateEvent($employeeId: String!, $eventTypeId: String!, $note: String, $timestamp: DateTime) {
    createEvent(employeeId: $employeeId, eventTypeId: $eventTypeId, note: $note, timestamp: $timestamp) {
      id
      timestamp
      note
      employee {
        id
        firstName
        lastName
        department
      }
      eventType {
        id
        name
        code
        color
      }
    }
  }
`;

export const BULK_IMPORT_EMPLOYEES = gql`
  mutation BulkImportEmployees($data: BulkImportEmployeesInput!) {
    bulkImportEmployees(data: $data) {
      successful {
        success
        employee {
          id
          email
          login
          firstName
          lastName
          pin
        }
      }
      errors {
        email
        error
      }
      totalProcessed
      successCount
      errorCount
    }
  }
`;

export const GET_DASHBOARD_STATISTICS = gql`
  query GetDashboardStatistics {
    dashboardStatistics {
      totalEmployees
      activeEmployees
      todayEvents
      averageWorkHours
    }
  }
`;

export const GET_EMPLOYEE_QR_CODE = gql`
  query GetEmployeeQRCode($employeeId: String!) {
    employeeQRCode(employeeId: $employeeId) {
      employeeId
      qrData
      timestamp
    }
  }
`;

export const REGENERATE_EMPLOYEE_QR_CODE = gql`
  mutation RegenerateEmployeeQRCode($employeeId: String!) {
    regenerateEmployeeQRCode(employeeId: $employeeId) {
      employeeId
      qrData
      timestamp
    }
  }
`;

export const EVENT_CREATED_SUBSCRIPTION = gql`
  subscription EventCreated {
    eventCreated {
      id
      timestamp
      note
      duration
      employee {
        id
        firstName
        lastName
        department
      }
      eventType {
        id
        name
        code
        color
      }
    }
  }
`;

export const GET_SYSTEM_SETTINGS = gql`
  query GetSystemSettings {
    systemSettings {
      id
      workDurationLimitMinutes
      breakDurationLimitMinutes
      smokeDurationLimitMinutes
      maxSmokeBreakMinutes
      leaveApprovalRequired
      leaveApproverIds
      deviceLogRetentionDays
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_SYSTEM_SETTINGS = gql`
  mutation UpdateSystemSettings($data: UpdateSystemSettingsInput!) {
    updateSystemSettings(data: $data) {
      id
      workDurationLimitMinutes
      breakDurationLimitMinutes
      smokeDurationLimitMinutes
      maxSmokeBreakMinutes
      leaveApprovalRequired
      leaveApproverIds
      deviceLogRetentionDays
      updatedAt
    }
  }
`;

// Changelog Queries
export const GET_UNREAD_HIGH_IMPORTANCE_CHANGELOGS = gql`
  query GetUnreadHighImportanceChangelogs {
    unreadHighImportanceChangelogs {
      id
      version
      title
      description
      category
      importance
      publishedAt
      isRead
    }
  }
`;

export const GET_ALL_CHANGELOGS = gql`
  query GetAllChangelogs {
    changelogs {
      id
      version
      title
      description
      category
      importance
      publishedAt
      isRead
    }
  }
`;

export const GET_UNREAD_CHANGELOG_COUNT = gql`
  query GetUnreadChangelogCount {
    unreadChangelogCount
  }
`;

export const MARK_CHANGELOG_AS_READ = gql`
  mutation MarkChangelogAsRead($input: MarkAsReadInput!) {
    markChangelogAsRead(input: $input) {
      id
      version
      title
    }
  }
`;

export const MARK_ALL_CHANGELOGS_AS_READ = gql`
  mutation MarkAllChangelogsAsRead {
    markAllChangelogsAsRead
  }
`;

// ============================================
// ADMIN: Changelog Management
// ============================================

export const GET_ALL_CHANGELOGS_ADMIN = gql`
  query GetAllChangelogsAdmin {
    allChangelogsAdmin {
      id
      version
      title
      description
      category
      importance
      requiredPermission
      isPublished
      publishedAt
      createdAt
    }
  }
`;

export const CREATE_CHANGELOG_ENTRY = gql`
  mutation CreateChangelogEntry($data: CreateChangelogInput!) {
    createChangelogEntry(data: $data) {
      id
      version
      title
      description
      category
      importance
      requiredPermission
      isPublished
      createdAt
    }
  }
`;

export const UPDATE_CHANGELOG_ENTRY = gql`
  mutation UpdateChangelogEntry($data: UpdateChangelogInput!) {
    updateChangelogEntry(data: $data) {
      id
      version
      title
      description
      category
      importance
      requiredPermission
      isPublished
      publishedAt
      createdAt
    }
  }
`;

export const PUBLISH_CHANGELOG_ENTRY = gql`
  mutation PublishChangelogEntry($id: ID!) {
    publishChangelogEntry(id: $id) {
      id
      isPublished
      publishedAt
    }
  }
`;

export const UNPUBLISH_CHANGELOG_ENTRY = gql`
  mutation UnpublishChangelogEntry($id: ID!) {
    unpublishChangelogEntry(id: $id) {
      id
      isPublished
      publishedAt
    }
  }
`;

export const DELETE_CHANGELOG_ENTRY = gql`
  mutation DeleteChangelogEntry($id: ID!) {
    deleteChangelogEntry(id: $id) {
      id
    }
  }
`;

export const EXPORT_DAILY_WORK_REPORT_CSV = gql`
  query ExportDailyWorkReportCSV(
    $startDate: DateTime!
    $endDate: DateTime!
    $employeeId: String
  ) {
    exportDailyWorkReportCSV(
      startDate: $startDate
      endDate: $endDate
      employeeId: $employeeId
    )
  }
`;

// Holiday queries and mutations
export const GET_HOLIDAYS = gql`
  query GetHolidays {
    holidays {
      id
      name
      date
      isRecurring
      description
      createdAt
      updatedAt
    }
  }
`;

export const GET_HOLIDAYS_BY_DATE_RANGE = gql`
  query GetHolidaysByDateRange($startDate: DateTime!, $endDate: DateTime!) {
    holidaysByDateRange(startDate: $startDate, endDate: $endDate) {
      id
      name
      date
      isRecurring
      description
    }
  }
`;

export const CREATE_HOLIDAY = gql`
  mutation CreateHoliday(
    $name: String!
    $date: DateTime!
    $isRecurring: Boolean
    $description: String
  ) {
    createHoliday(
      name: $name
      date: $date
      isRecurring: $isRecurring
      description: $description
    ) {
      id
      name
      date
      isRecurring
      description
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_HOLIDAY = gql`
  mutation UpdateHoliday(
    $id: String!
    $name: String
    $date: DateTime
    $isRecurring: Boolean
    $description: String
  ) {
    updateHoliday(
      id: $id
      name: $name
      date: $date
      isRecurring: $isRecurring
      description: $description
    ) {
      id
      name
      date
      isRecurring
      description
      updatedAt
    }
  }
`;

export const DELETE_HOLIDAY = gql`
  mutation DeleteHoliday($id: String!) {
    deleteHoliday(id: $id) {
      id
    }
  }
`;

// WorkDay queries and mutations (forced work days - e.g., Saturday make-up days)
export const GET_WORK_DAYS = gql`
  query GetWorkDays {
    workDays {
      id
      name
      date
      description
      createdAt
      updatedAt
    }
  }
`;

export const GET_WORK_DAYS_BY_DATE_RANGE = gql`
  query GetWorkDaysByDateRange($startDate: DateTime!, $endDate: DateTime!) {
    workDaysByDateRange(startDate: $startDate, endDate: $endDate) {
      id
      name
      date
      description
    }
  }
`;

export const CREATE_WORK_DAY = gql`
  mutation CreateWorkDay(
    $name: String!
    $date: DateTime!
    $description: String
  ) {
    createWorkDay(
      name: $name
      date: $date
      description: $description
    ) {
      id
      name
      date
      description
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_WORK_DAY = gql`
  mutation UpdateWorkDay(
    $id: String!
    $name: String
    $date: DateTime
    $description: String
  ) {
    updateWorkDay(
      id: $id
      name: $name
      date: $date
      description: $description
    ) {
      id
      name
      date
      description
      updatedAt
    }
  }
`;

export const DELETE_WORK_DAY = gql`
  mutation DeleteWorkDay($id: String!) {
    deleteWorkDay(id: $id) {
      id
    }
  }
`;

// Shift queries and mutations
export const GET_SHIFTS = gql`
  query GetShifts {
    shifts {
      id
      name
      description
      isDefault
      isActive
      lateArrivalToleranceMinutes
      earlyDepartureToleranceMinutes
      earlyArrivalToleranceMinutes
      lateDepartureToleranceMinutes
      employeeCount
      shiftTimes {
        id
        startTime
        endTime
        daysOfWeek
        order
        autoBreak
        autoBreakStartTime
        autoBreakEndTime
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_SHIFT = gql`
  query GetShift($id: String!) {
    shift(id: $id) {
      id
      name
      description
      isDefault
      isActive
      lateArrivalToleranceMinutes
      earlyDepartureToleranceMinutes
      earlyArrivalToleranceMinutes
      lateDepartureToleranceMinutes
      shiftTimes {
        id
        startTime
        endTime
        daysOfWeek
        order
        autoBreak
        autoBreakStartTime
        autoBreakEndTime
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_DEFAULT_SHIFT = gql`
  query GetDefaultShift {
    defaultShift {
      id
      name
      description
      shiftTimes {
        id
        startTime
        endTime
        daysOfWeek
        order
      }
    }
  }
`;

export const GET_SHIFT_TIME_FOR_EMPLOYEE = gql`
  query GetShiftTimeForEmployee($employeeId: String!, $timestamp: DateTime) {
    shiftTimeForEmployee(employeeId: $employeeId, timestamp: $timestamp) {
      shiftId
      shiftName
      startTime
      endTime
      daysOfWeek
      shiftIndex
      lateArrivalToleranceMinutes
      earlyDepartureToleranceMinutes
      earlyArrivalToleranceMinutes
      lateDepartureToleranceMinutes
    }
  }
`;

// Work Anomalies query
export const GET_WORK_ANOMALIES = gql`
  query GetWorkAnomalies($startDate: DateTime!, $endDate: DateTime!, $employeeId: String, $showAcknowledged: Boolean, $employeeIds: [String!]) {
    workAnomalies(startDate: $startDate, endDate: $endDate, employeeId: $employeeId, showAcknowledged: $showAcknowledged, employeeIds: $employeeIds) {
      employee {
        id
        firstName
        lastName
        department
        position
      }
      anomalies {
        type
        severity
        message
        date
        timestamp
        acknowledged
        acknowledgement {
          id
          employeeId
          date
          anomalyType
          acknowledgedBy
          acknowledgedByEmail
          note
          createdAt
        }
      }
      totalAnomalies
      highCount
      mediumCount
      lowCount
    }
  }
`;

export const GET_ANOMALY_COUNTS = gql`
  query GetAnomalyCounts($startDate: DateTime!, $endDate: DateTime!) {
    anomalyCounts(startDate: $startDate, endDate: $endDate) {
      total
      high
      medium
      low
      employeeCount
    }
  }
`;

export const ACKNOWLEDGE_ANOMALY = gql`
  mutation AcknowledgeAnomaly($employeeId: String!, $date: String!, $anomalyType: String!, $note: String) {
    acknowledgeAnomaly(employeeId: $employeeId, date: $date, anomalyType: $anomalyType, note: $note) {
      id
      employeeId
      date
      anomalyType
      acknowledgedBy
      acknowledgedByEmail
      note
      createdAt
    }
  }
`;

export const REVOKE_ANOMALY_ACKNOWLEDGEMENT = gql`
  mutation RevokeAnomalyAcknowledgement($id: String!) {
    revokeAnomalyAcknowledgement(id: $id)
  }
`;

// Documentation queries
export const GET_HOUR_CALCULATION_DOCS = gql`
  query GetHourCalculationDocs($includeTechnical: Boolean) {
    hourCalculationDocumentation(includeTechnical: $includeTechnical) {
      content
      title
    }
  }
`;

export const GET_AVAILABLE_DOCUMENTS = gql`
  query GetAvailableDocuments {
    availableDocuments {
      slug
      title
    }
  }
`;

// Department queries
export const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
      description
      isActive
      positionCount
      employeeCount
    }
  }
`;

export const GET_DEPARTMENTS_PAGINATED = gql`
  query GetDepartmentsPaginated($page: Int, $pageSize: Int, $sortBy: String, $sortOrder: String, $searchTerm: String) {
    departmentsPaginated(page: $page, pageSize: $pageSize, sortBy: $sortBy, sortOrder: $sortOrder, searchTerm: $searchTerm) {
      departments {
        id
        name
        description
        isActive
        positionCount
        employeeCount
        createdAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export const GET_DEPARTMENT_STATISTICS = gql`
  query GetDepartmentStatistics {
    departmentStatistics {
      totalDepartments
      activeDepartments
      departmentsWithEmployees
      emptyDepartments
    }
  }
`;

export const CREATE_DEPARTMENT_MUTATION = gql`
  mutation CreateDepartment($data: CreateDepartmentInput!) {
    createDepartment(data: $data) {
      id
      name
      description
      isActive
      positionCount
      employeeCount
    }
  }
`;

export const UPDATE_DEPARTMENT_MUTATION = gql`
  mutation UpdateDepartment($id: String!, $data: UpdateDepartmentInput!) {
    updateDepartment(id: $id, data: $data) {
      id
      name
      description
      isActive
      positionCount
      employeeCount
    }
  }
`;

export const DELETE_DEPARTMENT_MUTATION = gql`
  mutation DeleteDepartment($id: String!) {
    deleteDepartment(id: $id) {
      id
      name
    }
  }
`;

// Disability Degree queries and mutations
export const GET_DISABILITY_DEGREES = gql`
  query GetDisabilityDegrees {
    disabilityDegrees {
      id
      name
      maxWorkHoursPerDay
      breakDurationMinutes
      additionalBreakMinutes
      description
      isActive
      isDefault
      sortOrder
      employeeCount
    }
  }
`;

export const CREATE_DISABILITY_DEGREE_MUTATION = gql`
  mutation CreateDisabilityDegree($data: CreateDisabilityDegreeInput!) {
    createDisabilityDegree(data: $data) {
      id
      name
      maxWorkHoursPerDay
      breakDurationMinutes
      additionalBreakMinutes
      description
      isActive
      isDefault
      sortOrder
      employeeCount
    }
  }
`;

export const UPDATE_DISABILITY_DEGREE_MUTATION = gql`
  mutation UpdateDisabilityDegree($id: String!, $data: UpdateDisabilityDegreeInput!) {
    updateDisabilityDegree(id: $id, data: $data) {
      id
      name
      maxWorkHoursPerDay
      breakDurationMinutes
      additionalBreakMinutes
      description
      isActive
      isDefault
      sortOrder
      employeeCount
    }
  }
`;

export const DELETE_DISABILITY_DEGREE_MUTATION = gql`
  mutation DeleteDisabilityDegree($id: String!) {
    deleteDisabilityDegree(id: $id) {
      id
      name
    }
  }
`;

// ============================================
// ROLES & PERMISSIONS
// ============================================

// Lightweight roles list — used in EmployeeDialog role selector
export const GET_ROLES_BASIC = gql`
  query GetRolesBasic {
    roles {
      id
      name
      isSystem
      permissions
    }
  }
`;

// Full roles list — used in RolesPage
export const GET_ROLES = gql`
  query GetRoles {
    roles {
      id
      name
      description
      isSystem
      isOwnerRole
      permissions
      employeeCount
      createdAt
      updatedAt
    }
  }
`;

export const GET_PERMISSION_REGISTRY = gql`
  query GetPermissionRegistry {
    permissionRegistry {
      slug
      group
      label
      description
    }
  }
`;

export const CREATE_ROLE_MUTATION = gql`
  mutation CreateRole($data: CreateRoleInput!) {
    createRole(data: $data) {
      id
      name
      description
      isSystem
      isOwnerRole
      permissions
      employeeCount
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_ROLE_MUTATION = gql`
  mutation UpdateRole($data: UpdateRoleInput!) {
    updateRole(data: $data) {
      id
      name
      description
      isSystem
      isOwnerRole
      permissions
      employeeCount
      updatedAt
    }
  }
`;

export const UPDATE_ROLE_PERMISSIONS_MUTATION = gql`
  mutation UpdateRolePermissions($data: UpdateRolePermissionsInput!) {
    updateRolePermissions(data: $data) {
      id
      name
      permissions
      updatedAt
    }
  }
`;

export const DELETE_ROLE_MUTATION = gql`
  mutation DeleteRole($id: String!) {
    deleteRole(id: $id)
  }
`;
// =============================================================================
// PROJECTS + KANBAN
// =============================================================================

const PROJECT_BASE_FRAGMENT = gql`
  fragment ProjectBase on Project {
    id
    name
    description
    status
    startDate
    plannedEndDate
    leaderId
    createdById
    createdAt
    updatedAt
    leader {
      id
      firstName
      lastName
      email
    }
    memberCount
    taskCount
    doneTaskCount
  }
`;

const PROJECT_COLUMN_FRAGMENT = gql`
  fragment ProjectColumnBase on ProjectColumn {
    id
    projectId
    name
    color
    order
    isTerminal
  }
`;

const TASK_BASE_FRAGMENT = gql`
  fragment TaskBase on Task {
    id
    projectId
    columnId
    name
    description
    priority
    startDate
    plannedEndDate
    orderInColumn
    createdById
    createdAt
    updatedAt
    commentsCount
    unreadCommentsCount
    assignees {
      id
      employeeId
      employee {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const GET_PROJECTS_PAGINATED = gql`
  ${PROJECT_BASE_FRAGMENT}
  query GetProjectsPaginated(
    $page: Int
    $pageSize: Int
    $sortBy: String
    $sortOrder: String
    $search: String
    $statusFilter: [ProjectStatus!]
    $leaderIdFilter: String
    $includeArchived: Boolean
  ) {
    projectsPaginated(
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
      search: $search
      statusFilter: $statusFilter
      leaderIdFilter: $leaderIdFilter
      includeArchived: $includeArchived
    ) {
      items {
        ...ProjectBase
      }
      total
      page
      pageSize
    }
  }
`;

export const GET_MY_PROJECTS = gql`
  ${PROJECT_BASE_FRAGMENT}
  query GetMyProjects {
    myProjects {
      ...ProjectBase
    }
  }
`;

export const GET_MY_TASKS = gql`
  ${TASK_BASE_FRAGMENT}
  query GetMyTasks($limit: Int) {
    myTasks(limit: $limit) {
      ...TaskBase
      project {
        id
        name
        status
      }
      column {
        id
        name
        isTerminal
      }
    }
  }
`;

export const GET_PROJECT = gql`
  ${PROJECT_BASE_FRAGMENT}
  ${PROJECT_COLUMN_FRAGMENT}
  query GetProject($id: String!) {
    project(id: $id) {
      ...ProjectBase
      columns {
        ...ProjectColumnBase
      }
      members {
        id
        employeeId
        joinedAt
        employee {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const GET_PROJECT_MEMBERS = gql`
  query GetProjectMembers($projectId: String!) {
    projectMembers(projectId: $projectId) {
      id
      firstName
      lastName
      email
    }
  }
`;

export const GET_PROJECT_COLUMNS = gql`
  ${PROJECT_COLUMN_FRAGMENT}
  query GetProjectColumns($projectId: String!) {
    projectColumns(projectId: $projectId) {
      ...ProjectColumnBase
    }
  }
`;

export const GET_PROJECT_TASKS = gql`
  ${TASK_BASE_FRAGMENT}
  query GetProjectTasks($projectId: String!, $columnId: String) {
    projectTasks(projectId: $projectId, columnId: $columnId) {
      ...TaskBase
    }
  }
`;

export const GET_TASK = gql`
  ${TASK_BASE_FRAGMENT}
  query GetTask($id: String!) {
    task(id: $id) {
      ...TaskBase
      column {
        id
        name
      }
      project {
        id
        name
      }
    }
  }
`;

export const CREATE_PROJECT_MUTATION = gql`
  ${PROJECT_BASE_FRAGMENT}
  ${PROJECT_COLUMN_FRAGMENT}
  mutation CreateProject($input: CreateProjectInput!) {
    createProject(input: $input) {
      ...ProjectBase
      columns {
        ...ProjectColumnBase
      }
    }
  }
`;

export const UPDATE_PROJECT_MUTATION = gql`
  ${PROJECT_BASE_FRAGMENT}
  mutation UpdateProject($id: String!, $input: UpdateProjectInput!) {
    updateProject(id: $id, input: $input) {
      ...ProjectBase
    }
  }
`;

export const DELETE_PROJECT_MUTATION = gql`
  mutation DeleteProject($id: String!) {
    deleteProject(id: $id)
  }
`;

export const SET_PROJECT_MEMBERS_MUTATION = gql`
  ${PROJECT_BASE_FRAGMENT}
  mutation SetProjectMembers($input: SetProjectMembersInput!) {
    setProjectMembers(input: $input) {
      ...ProjectBase
      members {
        id
        employeeId
        employee {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const REMOVE_PROJECT_MEMBER_MUTATION = gql`
  ${PROJECT_BASE_FRAGMENT}
  mutation RemoveProjectMember($input: RemoveProjectMemberInput!) {
    removeProjectMember(input: $input) {
      ...ProjectBase
      members {
        id
        employeeId
        employee {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const CREATE_COLUMN_MUTATION = gql`
  ${PROJECT_COLUMN_FRAGMENT}
  mutation CreateColumn($input: CreateColumnInput!) {
    createColumn(input: $input) {
      ...ProjectColumnBase
    }
  }
`;

export const UPDATE_COLUMN_MUTATION = gql`
  ${PROJECT_COLUMN_FRAGMENT}
  mutation UpdateColumn($id: String!, $input: UpdateColumnInput!) {
    updateColumn(id: $id, input: $input) {
      ...ProjectColumnBase
    }
  }
`;

export const DELETE_COLUMN_MUTATION = gql`
  mutation DeleteColumn($id: String!, $moveTasksToColumnId: String) {
    deleteColumn(id: $id, moveTasksToColumnId: $moveTasksToColumnId)
  }
`;

export const REORDER_COLUMNS_MUTATION = gql`
  ${PROJECT_COLUMN_FRAGMENT}
  mutation ReorderColumns($input: ReorderColumnsInput!) {
    reorderColumns(input: $input) {
      ...ProjectColumnBase
    }
  }
`;

export const CREATE_TASK_MUTATION = gql`
  ${TASK_BASE_FRAGMENT}
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(input: $input) {
      ...TaskBase
    }
  }
`;

export const UPDATE_TASK_MUTATION = gql`
  ${TASK_BASE_FRAGMENT}
  mutation UpdateTask($id: String!, $input: UpdateTaskInput!) {
    updateTask(id: $id, input: $input) {
      ...TaskBase
    }
  }
`;

export const DELETE_TASK_MUTATION = gql`
  mutation DeleteTask($id: String!) {
    deleteTask(id: $id)
  }
`;

export const MOVE_TASK_MUTATION = gql`
  ${TASK_BASE_FRAGMENT}
  mutation MoveTask($input: MoveTaskInput!) {
    moveTask(input: $input) {
      ...TaskBase
    }
  }
`;

export const SET_TASK_ASSIGNEES_MUTATION = gql`
  ${TASK_BASE_FRAGMENT}
  mutation SetTaskAssignees($input: SetTaskAssigneesInput!) {
    setTaskAssignees(input: $input) {
      ...TaskBase
    }
  }
`;

export const PROJECT_BOARD_CHANGED_SUBSCRIPTION = gql`
  subscription ProjectBoardChanged($projectId: String!) {
    projectBoardChanged(projectId: $projectId) {
      type
      projectId
      payload
    }
  }
`;

// ========== Task comments ==========

export const TASK_COMMENT_FRAGMENT = gql`
  fragment TaskCommentBase on TaskComment {
    id
    taskId
    authorId
    content
    createdAt
    updatedAt
    author {
      id
      firstName
      lastName
    }
    mentions {
      id
      mentionedEmployeeId
      readAt
    }
  }
`;

export const GET_TASK_COMMENTS = gql`
  ${TASK_COMMENT_FRAGMENT}
  query TaskComments($taskId: String!) {
    taskComments(taskId: $taskId) {
      ...TaskCommentBase
    }
  }
`;

export const CREATE_TASK_COMMENT_MUTATION = gql`
  ${TASK_COMMENT_FRAGMENT}
  mutation CreateTaskComment($input: CreateTaskCommentInput!) {
    createTaskComment(input: $input) {
      ...TaskCommentBase
    }
  }
`;

export const UPDATE_TASK_COMMENT_MUTATION = gql`
  ${TASK_COMMENT_FRAGMENT}
  mutation UpdateTaskComment($id: String!, $input: UpdateTaskCommentInput!) {
    updateTaskComment(id: $id, input: $input) {
      ...TaskCommentBase
    }
  }
`;

export const DELETE_TASK_COMMENT_MUTATION = gql`
  mutation DeleteTaskComment($id: String!) {
    deleteTaskComment(id: $id)
  }
`;

export const SIGNAL_TASK_COMMENT_TYPING_MUTATION = gql`
  mutation SignalTaskCommentTyping($taskId: String!, $isTyping: Boolean!) {
    signalTaskCommentTyping(taskId: $taskId, isTyping: $isTyping)
  }
`;

export const MARK_TASK_SEEN_MUTATION = gql`
  mutation MarkTaskSeen($taskId: String!) {
    markTaskSeen(taskId: $taskId)
  }
`;

// ========== Mentions ==========

export const GET_MY_MENTIONS = gql`
  query MyMentions($unreadOnly: Boolean) {
    myMentions(unreadOnly: $unreadOnly) {
      id
      commentId
      taskId
      taskName
      projectId
      projectName
      authorId
      authorFirstName
      authorLastName
      contentExcerpt
      readAt
      createdAt
    }
  }
`;

export const GET_MY_UNREAD_MENTION_COUNT = gql`
  query MyUnreadMentionCount {
    myUnreadMentionCount
  }
`;

export const MARK_MENTION_READ_MUTATION = gql`
  mutation MarkMentionRead($id: String!) {
    markMentionRead(id: $id)
  }
`;

export const MARK_ALL_MENTIONS_READ_MUTATION = gql`
  mutation MarkAllMentionsRead {
    markAllMentionsRead
  }
`;

export const MY_MENTIONS_CHANGED_SUBSCRIPTION = gql`
  subscription MyMentionsChanged($employeeId: String!) {
    myMentionsChanged(employeeId: $employeeId) {
      employeeId
      unreadCount
    }
  }
`;

// Planning (weekly staffing plan) queries and mutations
export const STAFFING_PLAN_BOARD_FIELDS = gql`
  fragment StaffingPlanBoardFields on StaffingPlanBoard {
    weekStart
    dates
    workDays
    plan {
      id
      weekStart
      status
      version
      publishedAt
      publishedByName
      lastGeneratedAt
      updatedAt
    }
    employees {
      employeeId
      firstName
      lastName
      departmentId
      departmentName
      positionId
      positionName
      shiftId
      shiftName
      allowedSlotIds
    }
    slots {
      id
      shiftId
      shiftName
      startTime
      endTime
      daysOfWeek
      order
    }
    assignments {
      id
      employeeId
      date
      shiftTimeId
      slotStart
      slotEnd
      isPinned
      source
      note
    }
    absences {
      employeeId
      date
      label
      status
    }
    holidays {
      date
      name
    }
    headcount {
      positionId
      shiftTimeId
      date
      minHeadcount
      targetHeadcount
      planned
    }
    violations {
      code
      severity
      message
      employeeId
      date
      positionId
      shiftTimeId
    }
    constraints {
      id
      type
      status
      origin
      description
      employeeId
      positionId
      shiftTimeId
      dateFrom
      dateTo
      minHeadcount
      targetHeadcount
      reason
      originalText
      alternativeGroupId
      createdAt
    }
  }
`;

export const GET_STAFFING_PLAN_BOARD = gql`
  query GetStaffingPlanBoard($weekStart: String!) {
    staffingPlanBoard(weekStart: $weekStart) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const CREATE_STAFFING_PLAN_MUTATION = gql`
  mutation CreateStaffingPlan($weekStart: String!) {
    createStaffingPlan(weekStart: $weekStart) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const SET_PLAN_ASSIGNMENT_MUTATION = gql`
  mutation SetPlanAssignment($input: SetPlanAssignmentInput!) {
    setPlanAssignment(input: $input) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const CLEAR_PLAN_ASSIGNMENT_MUTATION = gql`
  mutation ClearPlanAssignment($input: ClearPlanAssignmentInput!) {
    clearPlanAssignment(input: $input) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const SET_PLAN_ASSIGNMENT_PIN_MUTATION = gql`
  mutation SetPlanAssignmentPin($input: SetPlanAssignmentPinInput!) {
    setPlanAssignmentPin(input: $input) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const PUBLISH_STAFFING_PLAN_MUTATION = gql`
  mutation PublishStaffingPlan($planId: String!) {
    publishStaffingPlan(planId: $planId) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const UNPUBLISH_STAFFING_PLAN_MUTATION = gql`
  mutation UnpublishStaffingPlan($planId: String!) {
    unpublishStaffingPlan(planId: $planId) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const DELETE_STAFFING_PLAN_MUTATION = gql`
  mutation DeleteStaffingPlan($planId: String!) {
    deleteStaffingPlan(planId: $planId)
  }
`;

export const GET_STAFFING_REQUIREMENTS_MATRIX = gql`
  query GetStaffingRequirementsMatrix {
    staffingRequirementsMatrix {
      positions {
        positionId
        positionName
        departmentName
        employeeCount
        slotIds
      }
      slots {
        id
        shiftId
        shiftName
        startTime
        endTime
        daysOfWeek
        order
      }
      requirements {
        id
        positionId
        shiftTimeId
        dayOfWeek
        minHeadcount
        targetHeadcount
      }
    }
  }
`;

export const SAVE_STAFFING_REQUIREMENTS_MUTATION = gql`
  mutation SaveStaffingRequirements($input: SaveStaffingRequirementsInput!) {
    saveStaffingRequirements(input: $input) {
      id
      positionId
      shiftTimeId
      dayOfWeek
      minHeadcount
      targetHeadcount
    }
  }
`;

export const GENERATE_STAFFING_PLAN_MUTATION = gql`
  mutation GenerateStaffingPlan($planId: String!, $mode: PlanGenerateMode!) {
    generateStaffingPlan(planId: $planId, mode: $mode) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const ADD_PLAN_CONSTRAINT_MUTATION = gql`
  mutation AddPlanConstraint($input: AddPlanConstraintInput!) {
    addPlanConstraint(input: $input) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const DELETE_PLAN_CONSTRAINT_MUTATION = gql`
  mutation DeletePlanConstraint($constraintId: String!) {
    deletePlanConstraint(constraintId: $constraintId) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const CONFIRM_PLAN_CONSTRAINT_MUTATION = gql`
  mutation ConfirmPlanConstraint($constraintId: String!) {
    confirmPlanConstraint(constraintId: $constraintId) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const REJECT_PLAN_CONSTRAINT_MUTATION = gql`
  mutation RejectPlanConstraint($constraintId: String!) {
    rejectPlanConstraint(constraintId: $constraintId) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const EXPORT_STAFFING_PLAN_MUTATION = gql`
  mutation ExportStaffingPlanToExcel($planId: String!) {
    exportStaffingPlanToExcel(planId: $planId)
  }
`;

export const PREVIEW_STAFFING_PLAN_IMPORT_MUTATION = gql`
  mutation PreviewStaffingPlanImport($planId: String!, $fileBase64: String!) {
    previewStaffingPlanImport(planId: $planId, fileBase64: $fileBase64) {
      unchangedCount
      changes {
        employeeId
        employeeName
        date
        from
        to
      }
      errors {
        row
        column
        message
      }
      violations {
        code
        severity
        message
        employeeId
        date
        positionId
        shiftTimeId
      }
    }
  }
`;

export const APPLY_STAFFING_PLAN_IMPORT_MUTATION = gql`
  mutation ApplyStaffingPlanImport($planId: String!, $fileBase64: String!) {
    applyStaffingPlanImport(planId: $planId, fileBase64: $fileBase64) {
      ...StaffingPlanBoardFields
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;

export const GET_PLANNING_AI_AVAILABLE = gql`
  query PlanningAiAvailable {
    planningAiAvailable
  }
`;

export const PROPOSE_PLAN_CONSTRAINTS_FROM_TEXT_MUTATION = gql`
  mutation ProposePlanConstraintsFromText($input: ProposePlanConstraintsInput!) {
    proposePlanConstraintsFromText(input: $input) {
      proposedCount
      issues {
        sourceText
        message
      }
      board {
        ...StaffingPlanBoardFields
      }
    }
  }
  ${STAFFING_PLAN_BOARD_FIELDS}
`;
